import { useState } from 'react'
import { Database, RefreshCw } from 'lucide-react'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { EvidenceReviewDialog } from '@/components/deals/evidence/EvidenceReviewDialog'
import { MarketEvidenceComparables } from '@/components/deals/evidence/MarketEvidenceComparables'
import { MarketEvidenceOverview } from '@/components/deals/evidence/MarketEvidenceOverview'
import {
  groupEvidence,
  presentationCategories,
} from '@/components/deals/evidence/evidence-presentation'
import { useDealEvidence } from '@/components/deals/evidence/use-deal-evidence'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'

function limitationMessage(location) {
  const limitations = location?.limitations ?? []
  if (limitations.includes('ni_location_source_not_enabled')) {
    return 'Northern Ireland location evidence is limited. Add or correct the address if more detail is available.'
  }
  if (location?.status && location.status !== 'resolved') {
    return 'Confirm the property postcode before Sentinel collects local evidence.'
  }
  return null
}

function precisionLabel(location) {
  if (location?.limitations?.includes('postcode_centred_not_building_verified')) {
    return 'Postcode centred, not building verified'
  }
  return location?.precision?.replaceAll('_', ' ') || 'Precision unavailable'
}

export function MarketEvidenceSection({ dealId, csrfToken }) {
  const state = useDealEvidence({ dealId, csrfToken })
  const [reviewEvidence, setReviewEvidence] = useState(null)
  const snapshot = state.evidence?.availableSnapshot
  const categories = snapshot?.categories ?? []
  const profiles = state.evidence?.context?.profiles
  const profileLabels = state.evidence?.context?.profileLabels
  const presentedCategories = presentationCategories(categories, profiles)
  const presentedGroups = groupEvidence(presentedCategories, profileLabels, profiles)
  const presentedCategoryCount = presentedGroups.reduce((total, group) => total + group.categories.length, 0)
  const location = state.evidence?.context?.location
  const limitation = limitationMessage(location)
  const canRefresh = state.evidence?.allowedActions?.collect?.allowed === true
  const canReview = (state.evidence?.suggestions?.length ?? 0) > 0
  const availability = state.evidence?.availability
  const pendingProfileIds = availability?.refreshPending
    ? availability.pendingProfileIds ?? []
    : []
  const pendingSet = new Set(pendingProfileIds.map(String))
  const unavailableProfileIds = (availability?.unavailableProfileIds ?? [])
    .filter((profileId) => !pendingSet.has(String(profileId)))
  const evidenceUpdating = state.isRefreshing || availability?.refreshPending === true

  const openReview = () => {
    state.clearConflict()
    setReviewEvidence(state.evidence)
  }

  const changeReviewOpen = (nextOpen) => {
    if (!nextOpen) setReviewEvidence(null)
  }

  return (
    <Card className="gap-0 py-0">
      <CardContent className="px-0">
        <Accordion type="single" collapsible>
          <AccordionItem value="market-evidence" className="border-0">
            <AccordionTrigger className="px-5 py-5 hover:no-underline">
              <span className="flex min-w-0 items-center gap-3">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Database className="size-4" aria-hidden="true" />
                </span>
                <span className="min-w-0 text-left">
                  <span className="block font-bold text-foreground">Market Evidence</span>
                  <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
                    {snapshot
                      ? `${presentedCategoryCount} evidence ${presentedCategoryCount === 1 ? 'category' : 'categories'}`
                      : 'Market evidence unavailable'}
                  </span>
                </span>
              </span>
            </AccordionTrigger>

            <AccordionContent className="border-t border-border px-5 pt-5">
              {state.isLoading && !snapshot ? (
                <p role="status" className="text-sm text-muted-foreground">Loading saved market evidence…</p>
              ) : null}
              {evidenceUpdating ? (
                <p role="status" className="mb-4 text-sm text-muted-foreground">
                  {availability?.retained
                    ? 'Refreshing changed profile evidence. Compatible saved evidence remains visible.'
                    : 'Refreshing market evidence. Existing evidence remains visible until complete.'}
                </p>
              ) : null}
              {state.error ? <p role="alert" className="mb-4 text-sm text-destructive">{state.error}</p> : null}
              {state.conflict ? <p role="alert" className="mb-4 text-sm text-destructive">{state.conflict}</p> : null}

              <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
                <div className="space-y-1 text-sm">
                  <p className="font-medium text-foreground">{location?.postcode || 'Postcode unavailable'}</p>
                  <p className="text-muted-foreground">{precisionLabel(location)}</p>
                  {snapshot?.publishedAt ? (
                    <p className="text-xs text-muted-foreground">Snapshot {snapshot.version} · {snapshot.publishedAt}</p>
                  ) : null}
                  {limitation ? <p className="text-sm text-muted-foreground">{limitation}</p> : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  {snapshot ? (
                    <Badge variant="secondary">
                      {availability?.retained ? 'Saved compatible evidence' : 'Saved evidence'}
                    </Badge>
                  ) : <Badge variant="outline">Unavailable</Badge>}
                  {canReview ? (
                    <Button type="button" variant="outline" size="sm" onClick={openReview}>
                      Review assumptions
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!canRefresh || state.isRefreshing}
                    onClick={() => { state.refresh().catch(() => undefined) }}
                  >
                    <RefreshCw className={cn(state.isRefreshing && 'animate-spin')} aria-hidden="true" />
                    Refresh evidence
                  </Button>
                </div>
              </div>

              {snapshot ? (
                <div className="space-y-3">
                  <div className="rounded-lg border border-border bg-muted/30 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Adopted assumptions</p>
                    <p className="mt-1 text-sm text-foreground">
                      {Object.keys(state.evidence?.adoptedBasis?.values ?? {}).length > 0
                        ? 'Saved separately from provider evidence.'
                        : 'No market assumptions adopted yet.'}
                    </p>
                  </div>
                  <Tabs defaultValue="overview" className="gap-4">
                    <TabsList aria-label="Market evidence views">
                      <TabsTrigger value="overview">Overview</TabsTrigger>
                      <TabsTrigger value="comparables">Comparables</TabsTrigger>
                    </TabsList>
                    <TabsContent value="overview">
                      <MarketEvidenceOverview
                        groups={presentedGroups}
                        pendingProfileIds={pendingProfileIds}
                        profileLabels={profileLabels}
                        profiles={profiles}
                        unavailableProfileIds={unavailableProfileIds}
                      />
                    </TabsContent>
                    <TabsContent value="comparables">
                      <MarketEvidenceComparables
                        key={snapshot.id}
                        groups={presentedGroups}
                        pendingProfileIds={pendingProfileIds}
                        profileLabels={profileLabels}
                        profiles={profiles}
                        unavailableProfileIds={unavailableProfileIds}
                      />
                    </TabsContent>
                  </Tabs>
                </div>
              ) : (
                <p className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
                  No supported market values are available. Sentinel will not substitute £0 or assumed figures.
                </p>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
      {reviewEvidence ? (
        <EvidenceReviewDialog
          open
          onOpenChange={changeReviewOpen}
          evidence={reviewEvidence}
          onSubmit={state.review}
          conflict={state.conflict}
        />
      ) : null}
    </Card>
  )
}
